import React from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip
} from '@mui/material'
import {
  Assessment,
  Download,
  Schedule,
  TrendingUp,
  Add,
  Warning,
  BarChart
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

export const ReportsPage = () => {
  const navigate = useNavigate()
  
  const reports = [
    {
      id: 1,
      title: 'MRD Files Summary',
      description: 'Complete summary of all MRD files with status breakdown',
      category: 'Files',
      frequency: 'Daily',
      lastGenerated: '2 hours ago'
    },
    {
      id: 2,
      title: 'Deficiency Report',
      description: 'Detailed report of deficiencies found and resolved',
      category: 'Quality',
      frequency: 'Weekly',
      lastGenerated: '1 day ago'
    },
    {
      id: 3,
      title: 'Medical Coding Statistics',
      description: 'Coding performance and accuracy metrics',
      category: 'Coding',
      frequency: 'Monthly',
      lastGenerated: '3 days ago'
    },
    {
      id: 4,
      title: 'File Movement Tracking',
      description: 'Track file movements across departments',
      category: 'Movement',
      frequency: 'Daily',
      lastGenerated: '5 hours ago'
    },
    {
      id: 5,
      title: 'Closure Analytics',
      description: 'File closure trends and completion rates',
      category: 'Closure',
      frequency: 'Weekly',
      lastGenerated: '2 days ago'
    },
    {
      id: 6,
      title: 'Department Performance',
      description: 'Performance metrics by department',
      category: 'Performance',
      frequency: 'Monthly',
      lastGenerated: '1 week ago'
    },
    {
      id: 7,
      title: 'Overdue Deficiencies',
      description: 'Files with deficiencies past their due dates',
      category: 'Quality',
      frequency: 'Real-time',
      lastGenerated: 'Live data',
      action: 'view',
      path: '/reports/overdue-deficiencies'
    },
    {
      id: 8,
      title: 'Deficiency Statistics',
      description: 'Comprehensive deficiency analytics and trends',
      category: 'Quality',
      frequency: 'Real-time',
      lastGenerated: 'Live data',
      action: 'view',
      path: '/reports/deficiency-statistics'
    }
  ]

  const getCategoryColor = (category) => {
    const colors = {
      'Files': 'primary',
      'Quality': 'success',
      'Coding': 'info',
      'Movement': 'warning',
      'Closure': 'secondary',
      'Performance': 'error'
    }
    return colors[category] || 'default'
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Reports & Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate and download various MRD system reports
        </Typography>
      </Box>

      {/* Reports Grid */}
      <Grid container spacing={3}>
        {reports.map((report) => (
          <Grid item xs={12} md={6} lg={4} key={report.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Assessment color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    {report.title}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {report.description}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={report.category}
                    color={getCategoryColor(report.category)}
                    size="small"
                  />
                  <Chip
                    label={report.frequency}
                    variant="outlined"
                    size="small"
                    icon={<Schedule />}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary">
                  Last generated: {report.lastGenerated}
                </Typography>
              </CardContent>
              
              <CardActions>
                {report.action === 'view' && report.path ? (
                  <Button
                    size="small"
                    startIcon={<TrendingUp />}
                    onClick={() => navigate(report.path)}
                  >
                    View Report
                  </Button>
                ) : (
                  <>
                    <Button
                      size="small"
                      startIcon={<TrendingUp />}
                      onClick={() => {
                        // Handle view report
                        console.log('View report:', report.id)
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => {
                        // Handle download report
                        console.log('Download report:', report.id)
                      }}
                    >
                      Download
                    </Button>
                  </>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Custom Report Section */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Custom Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Create custom reports with specific date ranges and filters
        </Typography>
        <Button variant="contained" startIcon={<Add />}>
          Create Custom Report
        </Button>
      </Paper>
    </Container>
  )
}

